/**
 * DowngradeConfirmModal — shown when a Pro user requests to downgrade to Free.
 * Requires explicit checkbox acknowledgment before submit enables.
 * Ticket #341
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, Button, Checkbox,
  FormControlLabel, CircularProgress, IconButton,
  Alert,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CloseIcon from '@mui/icons-material/Close';

interface DowngradeConfirmModalProps {
  open: boolean;
  onClose: () => void;
  fromPlan: string;
  toPlan: string;
  onConfirm: () => Promise<void>;
}

const DowngradeConfirmModal: React.FC<DowngradeConfirmModalProps> = ({
  open, onClose, fromPlan, toPlan, onConfirm,
}) => {
  const [ackStorage, setAckStorage] = useState(false);
  const [ackFeatures, setAckFeatures] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = ackStorage && ackFeatures && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e: any) {
      setError(e?.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setAckStorage(false);
    setAckFeatures(false);
    setError(null);
    onClose();
  };

  const fromLabel = fromPlan.charAt(0).toUpperCase() + fromPlan.slice(1);
  const toLabel = toPlan.charAt(0).toUpperCase() + toPlan.slice(1);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px' } }}
    >
      {/* Warning header */}
      <Box sx={{
        bgcolor: '#FFF3E0',
        borderRadius: '20px 20px 0 0',
        px: 3, pt: 3, pb: 2.5,
        position: 'relative',
        borderBottom: '1px solid #FFE0B2',
      }}>
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={submitting}
          sx={{ position: 'absolute', top: 12, right: 12, color: '#E65100', opacity: 0.7 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            bgcolor: '#FFB347',
            borderRadius: '50%', width: 44, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <WarningAmberOutlinedIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', fontFamily: 'Poppins, sans-serif', color: '#1A2826' }}>
              Confirm Downgrade
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#4A5E5C' }}>
              {fromLabel} → {toLabel}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
          Before you downgrade, please read and confirm you understand the following:
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>
        )}

        {/* Checkbox 1 — Storage */}
        <Box sx={{
          border: `2px solid ${ackStorage ? '#2D6E6A' : '#e0e0e0'}`,
          borderRadius: '12px', p: 2, mb: 2,
          bgcolor: ackStorage ? '#F0FAF9' : 'white',
          transition: 'all 0.2s',
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={ackStorage}
                onChange={(e) => setAckStorage(e.target.checked)}
                disabled={submitting}
                sx={{ color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' }, pt: 0 }}
              />
            }
            label={
              <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
                <strong>I understand that photos exceeding 1 GB of storage may be permanently removed</strong> when my plan is downgraded to Free.
              </Typography>
            }

            sx={{ m: 0, alignItems: 'flex-start' }}
          />
        </Box>

        {/* Checkbox 2 — Features */}
        <Box sx={{
          border: `2px solid ${ackFeatures ? '#2D6E6A' : '#e0e0e0'}`,
          borderRadius: '12px', p: 2, mb: 1,
          bgcolor: ackFeatures ? '#F0FAF9' : 'white',
          transition: 'all 0.2s',
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={ackFeatures}
                onChange={(e) => setAckFeatures(e.target.checked)}
                disabled={submitting}
                sx={{ color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' }, pt: 0 }}
              />
            }
            label={
              <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
                <strong>I understand that Trip Companions, expiration control, BFFs / Travel Partners visibility, and photo approval workflow will no longer be available</strong> on the Free plan.
              </Typography>
            }

            sx={{ m: 0, alignItems: 'flex-start' }}
          />
        </Box>

        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5, lineHeight: 1.5 }}>
          Your downgrade request will be processed by our team. You'll receive a confirmation email once complete.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleConfirm}
          disabled={!canSubmit}
          sx={{
            bgcolor: canSubmit ? '#E65100' : '#e0e0e0',
            color: canSubmit ? 'white' : '#9e9e9e',
            borderRadius: '24px',
            textTransform: 'none', fontWeight: 700, py: 1.25,
            '&:hover': canSubmit ? { bgcolor: '#BF360C' } : {},
            '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' },
          }}
        >
          {submitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Confirm Downgrade'}
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={handleClose}
          disabled={submitting}
          sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500 }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DowngradeConfirmModal;
